import { gql,useMutation,useQuery } from "@apollo/client";
import {
  useDeleteShopMetadataMutation,
  useUpdateMetadataMutation,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router";

import { CheckoutQuestionsFormData,SimpleCheckoutQuestionsPage } from "../components/SimpleCheckoutQuestionsPage";

interface CheckoutQuestionsParams {
  appType: string;
}

// Custom GraphQL query to fetch page by slug
const PAGE_BY_SLUG_QUERY = gql`
  query PageBySlug($slug: String!) {
    page(slug: $slug) {
      id
      title
      slug
      isPublished
      metadata {
        key
        value
      }
      privateMetadata {
        key
        value
      }
    }
  }
`;

// Add the UPDATE_PAGE mutation
const UPDATE_PAGE = gql`
  mutation UpdatePage($id: ID!, $input: PageInput!) {
    pageUpdate(id: $id, input: $input) {
      errors {
        field
        message
      }
      page {
        id
        title
        slug
        content
        seoTitle
        seoDescription
        isPublished
        metadata {
          key
          value
        }
      }
    }
  }
`;

const CheckoutQuestionsView: React.FC = () => {
  const { appType } = useParams<CheckoutQuestionsParams>();
  const intl = useIntl();
  const notify = useNotifier();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Fetch the page details using the slug from the route parameter
  const { data: pageData, loading: pageLoading, refetch } = useQuery(PAGE_BY_SLUG_QUERY, {
    variables: { 
      slug: appType, // appType contains "checkout-question"
    },
  });

  const [updateMetadata] = useUpdateMetadataMutation();
  const [deleteMetadata] = useDeleteShopMetadataMutation();
  const [updatePage] = useMutation(UPDATE_PAGE);

  // Parse metadata into form data
  const parseMetadataToFormData = (metadata: Array<{ key: string; value: string }>): CheckoutQuestionsFormData => {
    const defaultData: CheckoutQuestionsFormData = {
      reCAPTCHA: false,
      guest_only: false,
      order_value: "",
      questions: [],
      isActive: false,
    };

    metadata.forEach(({ key, value }) => {
      if (key === "reCAPTCHA") {
        defaultData.reCAPTCHA = value === "true";
      } else if (key === "guest_only") {
        defaultData.guest_only = value === "true";
      } else if (key === "order_value") {
        defaultData.order_value = value;
      } else if (key.startsWith("question-")) {
        // Parse question format: "r:Question text?:Option1,Option2"
        const parts = value.split(":");

        if (parts.length >= 2) {
          const required = parts[0] === "r";
          const questionText = parts[1];
          const options = parts[2] ? parts[2].split(",").map(opt => opt.trim()) : [];
          
          defaultData.questions.push({
            id: key,
            question: questionText,
            required,
            hasMultipleChoice: options.length > 0,
            options: options.length > 0 ? options : ["Option 1", "Option 2"],
          });
        }
      }
    });

    return defaultData;
  };

  // Convert form data back to metadata format
  const convertFormDataToMetadata = (data: CheckoutQuestionsFormData) => {
    const metadata: Array<{ key: string; value: string }> = [
      { key: "reCAPTCHA", value: data.reCAPTCHA.toString() },
      { key: "guest_only", value: data.guest_only.toString() },
      { key: "order_value", value: data.order_value },
    ];

    // Get existing question keys to avoid duplicates
    const existingKeys = new Set(pageData?.page?.metadata?.map((m: { key: string; value: string }) => m.key) || []);
    
    data.questions.forEach((question, index) => {
      let key: string;
      
      if (question.id.startsWith("question-")) {
        // Use existing question key
        key = question.id;
      } else {
        // Generate unique key for new questions
        let candidateKey = `question-${index + 1}`;
        let counter = 1;
        
        while (existingKeys.has(candidateKey) || metadata.some(m => m.key === candidateKey)) {
          counter++;
          candidateKey = `question-${counter}`;
        }
        
        key = candidateKey;
      }
      
      const prefix = question.required ? "r" : "nr";
      const optionsStr = question.hasMultipleChoice && question.options ? question.options.join(",") : "";
      const value = `${prefix}:${question.question}:${optionsStr}`;

      metadata.push({ key, value });
    });

    return metadata;
  };


  const initialData = pageData?.page?.metadata 
    ? { ...parseMetadataToFormData(pageData.page.metadata), isActive: pageData.page.isPublished || false }
    : {
        reCAPTCHA: false,
        guest_only: false,
        order_value: "",
        questions: [],
        isActive: false,
      };


  const handleSubmit = async (data: CheckoutQuestionsFormData) => {
    if (!pageData?.page?.id) {
      setErrors([`Page with slug '${appType}' not found. Please create this page first in the CMS.`]);

      return;
    }

    setLoading(true);
    setErrors([]);

    try {
      const metadata = convertFormDataToMetadata(data);
      
      // Get the current metadata keys to know which ones to delete
      const currentKeys = pageData.page.metadata?.map((m: any) => m.key) || [];
      const newKeys = metadata.map(m => m.key);
      const keysToDelete = currentKeys.filter((key: any) => 
        key.startsWith("question-") && !newKeys.includes(key)
      );

      // First, delete old question keys if any
      if (keysToDelete.length > 0) {
        const deleteResult = await deleteMetadata({
          variables: {
            id: pageData.page.id,
            keys: keysToDelete,
          },
        });
        
        if (deleteResult.data?.deleteMetadata?.errors?.length) {
          const deleteErrors = deleteResult.data.deleteMetadata.errors.map(error => error.message || "Unknown delete error");

          setErrors(deleteErrors);
          throw new Error(deleteErrors.join(", "));
        }
      }

      // Step 2: Update page status (isPublished)
      const pageResult = await updatePage({
        variables: {
          id: pageData.page.id,
          input: {
            isPublished: data.isActive,
          },
        },
      });

      if (pageResult.data?.pageUpdate?.errors?.length) {
        const pageErrors = pageResult.data.pageUpdate.errors.map((error: any) => error.message || "Unknown error");

        setErrors(pageErrors);
        throw new Error(pageErrors.join(", "));
      }

      // Step 3: Update metadata using the DYNAMIC ID from the page query (exactly as you specified)
      const result = await updateMetadata({
        variables: {
          id: pageData.page.id, // Using ID from page(slug: "checkout-question") query
          input: metadata,
          keysToDelete: [], // Empty since we handled deletions separately
        },
      });

      if (result.data?.updateMetadata?.errors?.length) {
        const errorMessages = result.data.updateMetadata.errors.map(error => error.message || "Unknown error");

        setErrors(errorMessages);
        throw new Error(errorMessages.join(", "));
      }

      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });

      // Refetch to get updated data
      refetch();
      
    } catch (error: any) {
      setErrors([`Failed to save configurations: ${error.message}`]);
      notify({
        status: "error",
        text: intl.formatMessage(commonMessages.somethingWentWrong),
      });
    } finally {
      setLoading(false);
    }
  };

  // Show loading while fetching page data
  if (pageLoading) {
    return <div style={{ padding: "20px" }}>Loading...</div>;
  }

  // Add page not found to errors if needed
  const effectiveErrors = !pageData?.page 
    ? [`Page with slug '${appType}' not found. Please create this page first in the CMS.`, ...errors]
    : errors;

  return (
    <SimpleCheckoutQuestionsPage
      disabled={loading || !pageData?.page}
      errors={effectiveErrors}
      onSubmit={handleSubmit}
      initialData={initialData}
    />
  );
};

export default CheckoutQuestionsView;